<template>
    <div class="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
        <div class="p-4 border-b border-gray-200 bg-gray-50">
            <h3 class="text-lg font-semibold text-gray-800">수급 현황</h3>
            <p class="text-xs text-gray-500 mt-1">
                {{ groupTypeLabel }} 기준 · 단위: 억원
            </p>
        </div>
        <div class="overflow-x-auto">
            <table class="w-full text-sm table-fixed">
                <colgroup>
                    <col style="width: 140px;">
                    <col v-for="bucket in maturityBuckets" :key="bucket.value" style="width: 120px;">
                </colgroup>
                <thead class="bg-gray-50 border-b">
                    <tr>
                        <th
                            class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                            {{ groupTypeLabel }}
                        </th>
                        <th v-for="bucket in maturityBuckets" :key="bucket.value"
                            class="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                            {{ bucket.label }}
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="row in tableData" :key="row.rowKey" class="hover:bg-gray-50 transition-colors">
                        <td
                            class="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap sticky left-0 bg-white">
                            {{ getRowLabel(row.rowKey) }}
                        </td>
                        <td v-for="bucket in maturityBuckets" :key="bucket.value"
                            @click="handleCellClick(row.rowKey, bucket.value)" :class="[
                                'px-4 py-3 text-center text-sm cursor-pointer transition-all relative',
                                getCellClass(row.cells[bucket.value]),
                                isSelectedCell(row.rowKey, bucket.value) && 'bg-blue-50',
                            ]">
                            <div v-if="isSelectedCell(row.rowKey, bucket.value)"
                                class="absolute inset-0 ring-2 ring-inset ring-blue-500 pointer-events-none"></div>
                            <span v-html="formatCellValue(row.cells[bucket.value])"></span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import { computed } from 'vue';
import {
    maturityBucketLabel,
    maturityBucketOrder,
    creditTypeLabel,
    institutionTypeLabel,
    GroupType,
} from '@/utils/marketFlowUtil';

export default {
    name: 'SupplyDemandTable',
    props: {
        tableData: {
            type: Array,
            required: true,
        },
        groupType: {
            type: String,
            required: true,
        },
        selectedCell: {
            type: Object,
            default: () => ({ rowKey: null, colKey: null }),
        },
        directionFilter: {
            type: Object,
            required: true,
        },
    },
    emits: ['cell-click'],
    setup(props, { emit }) {
        const maturityBuckets = maturityBucketOrder.map(value => ({
            value,
            label: maturityBucketLabel[value],
        }));

        const groupTypeLabel = computed(() => {
            return props.groupType === GroupType.BOND_TYPE ? '기관 유형' : '채권 유형';
        });

        const getRowLabel = rowKey => {
            if (props.groupType === GroupType.BOND_TYPE) {
                return institutionTypeLabel[rowKey] || rowKey;
            } else {
                return creditTypeLabel[rowKey] || rowKey;
            }
        };

        const formatCellValue = cellData => {
            if (!cellData) return '';

            const { buyAmount, sellAmount, netAmount } = cellData;
            const { buy, sell, net } = props.directionFilter;

            // 모두 0이면 빈칸
            if (buyAmount === 0 && sellAmount === 0) return '';

            const formatNet = (value) => {
                const rounded = Math.round(value);
                if (value > 0) {
                    return `<span class="text-red-600">+${rounded}</span>`;
                } else if (value < 0) {
                    return `<span class="text-blue-600">${rounded}</span>`;
                }
                return rounded.toString();
            };

            // 순매수만 선택
            if (net && !buy && !sell) {
                if (netAmount === 0) return '';
                return formatNet(netAmount);
            }

            // 매도만 선택
            if (sell && !buy && !net) {
                if (sellAmount === 0) return '';
                return `-${Math.round(sellAmount)}`;
            }

            // 매수만 선택
            if (buy && !sell && !net) {
                if (buyAmount === 0) return '';
                return `+${Math.round(buyAmount)}`;
            }

            // 매도 + 매수 선택
            if (buy && sell && !net) {
                if (buyAmount === 0 && sellAmount === 0) return '';
                return `-${Math.round(sellAmount)}/+${Math.round(buyAmount)}`;
            }

            // 매도 + 매수 + 순매수 선택 (모두 선택)
            if (buy && sell && net) {
                if (buyAmount === 0 && sellAmount === 0) return '';
                return `-${Math.round(sellAmount)}/+${Math.round(buyAmount)} (${formatNet(netAmount)})`;
            }

            // 매도 + 순매수
            if (sell && net && !buy) {
                if (sellAmount === 0) return '';
                return `-${Math.round(sellAmount)} (${formatNet(netAmount)})`;
            }

            // 매수 + 순매수
            if (buy && net && !sell) {
                if (buyAmount === 0) return '';
                return `+${Math.round(buyAmount)} (${formatNet(netAmount)})`;
            }

            return '';
        };

        const getCellClass = cellData => {
            if (!cellData) return 'text-gray-300';

            const { buyAmount, sellAmount, netAmount } = cellData;
            const { buy, sell, net } = props.directionFilter;

            // 모두 0이면 회색
            if (buyAmount === 0 && sellAmount === 0) return 'text-gray-300';

            // 순매수만 선택 시 색상 적용
            if (net && !buy && !sell) {
                if (netAmount === 0) return 'text-gray-300';
                if (netAmount > 0) return 'text-red-600 font-medium hover:bg-red-50';
                return 'text-blue-600 font-medium hover:bg-blue-50';
            }

            // 그 외의 경우 색상 없음
            return 'text-gray-700 font-medium hover:bg-gray-50';
        };

        const isSelectedCell = (rowKey, colKey) => {
            return (
                props.selectedCell.rowKey === rowKey &&
                props.selectedCell.colKey === colKey
            );
        };

        const handleCellClick = (rowKey, colKey) => {
            emit('cell-click', { rowKey, colKey });
        };

        return {
            maturityBuckets,
            groupTypeLabel,
            getRowLabel,
            formatCellValue,
            getCellClass,
            isSelectedCell,
            handleCellClick,
        };
    },
};
</script>
